import React from 'react';
import type { FormComponent as FormComponentType } from '../schemas/validation';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getFontFamily } from '../utils/fontLoader';
import { getThemeStyles } from '../styles/themes';
import { FormCore } from './FormCore';

interface FormComponentProps {
  component: FormComponentType;
}

export const FormComponent: React.FC<FormComponentProps> = ({ component }) => {
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);
  const brandFonts = useBrandFonts();

  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  const data = component.data;
  const bgColor =
    (data as any).customColors?.background ||
    data.styling?.backgroundColor ||
    themeStyles.form?.background ||
    '#ffffff';
  const textColor =
    (data as any).customColors?.text ||
    data.styling?.textColor ||
    themeStyles.form?.labelColor ||
    '#374151';
  const labelColor = (data as any).customColors?.label || textColor;
  const titleColor = (data as any).customColors?.title || labelColor;

  const alignmentClasses: Record<string, string> = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  const alignment = data.styling.alignment || 'center';

  return (
    <section className="py-16 md:py-24" style={{ backgroundColor: bgColor }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <div
            className={`max-w-4xl ${alignment === 'center' ? 'mx-auto' : alignment === 'right' ? 'ml-auto' : ''}`}
          >
            <h2
              className={`text-3xl md:text-4xl font-bold mb-4 ${alignmentClasses[alignment]}`}
              style={{ color: titleColor, ...headingFontStyle }}
            >
              {data.title}
            </h2>
            {data.description && (
              <p
                className={`text-lg max-w-2xl ${alignmentClasses[alignment]} ${alignment === 'center' ? 'mx-auto' : ''} opacity-80`}
                style={{ color: labelColor, ...bodyFontStyle }}
              >
                {data.description}
              </p>
            )}
          </div>
        </div>

        <div className="max-w-2xl mx-auto">
          <FormCore component={component} />
        </div>
      </div>
    </section>
  );
};
